import React, { useEffect, useRef, useState } from 'react'
import Icon from './IconLibrary'
import { NavLink, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext.jsx'
import './Navbar.css'

/**
 * Navbar
 * - Left: Hamburger button opens a slide-in tray from the left
 * - Right: Ellipsis vertical opens a dropdown menu, Person + "Sign in" opens a modal
 *
 * Notes:
 * - Uses CSS variables and supports light/dark via prefers-color-scheme
 * - No auth logic yet; the Sign in opens a placeholder modal for now
 */
export default function Navbar() {
  const [trayOpen, setTrayOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [signinOpen, setSigninOpen] = useState(false)
  const [menuView, setMenuView] = useState('root') // 'root' | 'appearance'

  // theme: 'system' | 'light' | 'dark'
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 'system'
  })

  const location = useLocation()
  const path = location.pathname
  const { user, login, register, forgotPassword, resendVerification, logout } = useAuth()
  const [authMode, setAuthMode] = useState('login') // 'login' | 'register'
  const [authLoading, setAuthLoading] = useState(false)
  const [authError, setAuthError] = useState('')
  const [authMessage, setAuthMessage] = useState('')
  const [form, setForm] = useState({ email: '', password: '', username: '' })

  const menuRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function onDocClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false)
        setMenuView('root')
      }
    }
    if (menuOpen) document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [menuOpen])

  // Lock body scroll when modals/tray open
  useEffect(() => {
    const open = trayOpen || signinOpen
    document.documentElement.style.overflow = open ? 'hidden' : ''
    return () => {
      document.documentElement.style.overflow = ''
    }
  }, [trayOpen, signinOpen])

  // Apply theme to <html data-theme="...">
  useEffect(() => {
    const root = document.documentElement
    const mq = window.matchMedia('(prefers-color-scheme: dark)')

    function applyTheme(current) {
      // Persist
      localStorage.setItem('theme', current)
      // Data attribute used by CSS overrides
      if (current === 'system') {
        root.dataset.theme = mq.matches ? 'dark' : 'light'
      } else {
        root.dataset.theme = current
      }
    }

    applyTheme(theme)
    // Update on system change when in system mode
    function onChange() {
      if (theme === 'system') applyTheme('system')
    }
    mq.addEventListener?.('change', onChange)
    return () => mq.removeEventListener?.('change', onChange)
  }, [theme])

  const themeLabel = theme === 'system' ? 'Device' : theme === 'dark' ? 'Dark' : 'Light'

  return (
    <header className="navbar">
      <div className="nav-left">
        <button
          className="icon-btn"
          aria-label="Open menu"
          onClick={() => setTrayOpen(true)}
        >
          {/* Inline SVG hamburger (simple three lines) */}
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="hamburger"
          >
            <rect x="3" y="6" width="18" height="2" rx="1" />
            <rect x="3" y="11" width="18" height="2" rx="1" />
            <rect x="3" y="16" width="18" height="2" rx="1" />
          </svg>
        </button>
      </div>

      <div className="nav-spacer" />

      <div className="nav-right">
        <div className="menu-wrapper" ref={menuRef}>
          {user ? (
            <button
              className="avatar-btn"
              title={user.username}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => { setMenuView('root'); setMenuOpen((v) => !v) }}
            >
              <span className="avatar-circle">{(user.username || user.email || '?').slice(0,1).toUpperCase()}</span>
            </button>
          ) : (
            <button
              className="icon-btn"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              <Icon name="ellipsis-vertical" size={22} />
            </button>
          )}

          {menuOpen && (
            <div className="dropdown" role="menu">
              {user && menuView === 'root' && (
                <div style={{display:'grid', gap:8, padding:'6px 6px 10px'}}>
                  <div className="dropdown-user">
                    <span className="avatar-circle lg">{(user.username || user.email || '?').slice(0,1).toUpperCase()}</span>
                    <div className="user-meta">
                      <div className="user-name">{user.username}</div>
                      <div className="user-email">{user.email}</div>
                    </div>
                  </div>
                  <div className="dropdown-divider" />
                  <button className="dropdown-row" onClick={async ()=>{ await logout(); setMenuOpen(false) }}>
                    <div className="row-left">
                      <Icon name="logout" size={20} />
                      <span>Sign Out</span>
                    </div>
                  </button>
                  <div className="dropdown-divider" />
                </div>
              )}

              {menuView === 'root' && (
                <button className="dropdown-row" onClick={() => setMenuView('appearance')}>
                  <div className="row-left">
                    <Icon name="appearance" size={20} />
                    <span>Appearance: {themeLabel} theme</span>
                  </div>
                  <Icon name="chevron-right" size={18} />
                </button>
              )}

              {menuView === 'appearance' && (
                <div className="dropdown-panel">
                  <div className="dropdown-header">
                    <button className="icon-btn" aria-label="Back" onClick={() => setMenuView('root')}>
                      <Icon name="arrow-left" size={18} />
                    </button>
                    <span>Appearance</span>
                    <span style={{ width: 40 }} />
                  </div>

                  <div className="dropdown-section-label">Select a theme</div>
                  <div className="dropdown-list">
                    <button
                      className="dropdown-item"
                      onClick={() => setTheme('system')}
                    >
                      <span className="item-left">Use device theme</span>
                      {theme === 'system' && <Icon name="checkmark" size={18} />}
                    </button>
                    <button
                      className="dropdown-item"
                      onClick={() => setTheme('dark')}
                    >
                      <span className="item-left">Dark theme</span>
                      {theme === 'dark' && <Icon name="checkmark" size={18} />}
                    </button>
                    <button
                      className="dropdown-item"
                      onClick={() => setTheme('light')}
                    >
                      <span className="item-left">Light theme</span>
                      {theme === 'light' && <Icon name="checkmark" size={18} />}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {!user && (
          <button className="signin-btn" onClick={() => { setAuthMode('login'); setSigninOpen(true) }}>
            <Icon name="person" size={18} />
            <span>Sign in</span>
          </button>
        )}
      </div>

      {/* Slide-in tray */}
      <div className={`tray-overlay ${trayOpen ? 'show' : ''}`} onClick={() => setTrayOpen(false)} />
      <aside className={`tray ${trayOpen ? 'open' : ''}`} aria-hidden={!trayOpen}>
        <div className="tray-header">
          <button
            className="icon-btn"
            aria-label="Close menu"
            onClick={() => setTrayOpen(false)}
          >
            {/* Same hamburger icon as the trigger (static, no morph) */}
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="hamburger"
            >
              <rect x="3" y="6" width="18" height="2" rx="1" />
              <rect x="3" y="11" width="18" height="2" rx="1" />
              <rect x="3" y="16" width="18" height="2" rx="1" />
            </svg>
          </button>
        </div>
        <nav className="tray-nav">
          <ul className="tray-list">
            <li>
              <NavLink
                to="/"
                className={({ isActive }) => `tray-item ${isActive ? 'active' : ''}`}
                onClick={() => setTrayOpen(false)}
              >
                <Icon name={path === '/' ? 'house.fill' : 'house'} size={22} />
                <span>Home</span>
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/songs"
                className={({ isActive }) => `tray-item ${isActive ? 'active' : ''}`}
                onClick={() => setTrayOpen(false)}
              >
                <Icon name={path === '/songs' ? 'music.note.tv.fill' : 'music.note.tv'} size={22} />
                <span>Songs</span>
              </NavLink>
            </li>
            <li className="tray-sep" aria-hidden="true" />
            <li>
              <NavLink
                to={user ? '/profile' : '/you'}
                className={({ isActive }) => `tray-item ${isActive ? 'active' : ''}`}
                onClick={() => setTrayOpen(false)}
              >
                <Icon name={(user ? path === '/profile' : path === '/you') ? 'person.fill' : 'person'} size={22} />
                <span>You</span>
              </NavLink>
            </li>
            {user?.role === 'superadmin' && (
              <>
                <li className="tray-sep" aria-hidden="true" />
                <li>
                  <NavLink
                    to="/upload"
                    className={({ isActive }) => `tray-item ${isActive ? 'active' : ''}`}
                    onClick={() => setTrayOpen(false)}
                  >
                    <Icon name={path === '/upload' ? 'square.and.arrow.up.fill' : 'square.and.arrow.up'} size={22} />
                    <span>Upload Video</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/transcoding"
                    className={({ isActive }) => `tray-item ${isActive ? 'active' : ''}`}
                    onClick={() => setTrayOpen(false)}
                  >
                    <Icon name={path === '/transcoding' ? 'chart-fill' : 'chart'} size={22} />
                    <span>Transcoding Status</span>
                  </NavLink>
                </li>
              </>
            )}
            <li className="tray-sep" aria-hidden="true" />
          </ul>

          {!user && (
            <div className="tray-signin">
              <p>Sign in to like videos, comment, and participate</p>
              <button className="signin-btn" onClick={() => setSigninOpen(true)}>
                <Icon name="person" size={18} />
                <span>Sign in</span>
              </button>
            </div>
          )}
        </nav>
      </aside>

      {/* Sign-in modal */}
      {signinOpen && (
        <div className="modal" role="dialog" aria-modal="true">
          <div className="modal-backdrop" onClick={() => setSigninOpen(false)} />
          <div className="modal-content">
            <div className="modal-header">
              <h3>{authMode === 'login' ? 'Sign in' : 'Create account'}</h3>
              <button className="icon-btn" aria-label="Close" onClick={() => setSigninOpen(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 6L18 18" strokeWidth="2" strokeLinecap="round" />
                  <path d="M18 6L6 18" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <form className="modal-body" onSubmit={async (e) => {
              e.preventDefault()
              setAuthError(''); setAuthMessage(''); setAuthLoading(true)
              try {
                if (authMode === 'login') {
                  await login({ email: form.email, password: form.password })
                  setSigninOpen(false)
                } else {
                  await register({ username: form.username, email: form.email, password: form.password })
                  setAuthMessage('Registered. Please check your email to verify your account.')
                  setAuthMode('login')
                }
              } catch (err) {
                setAuthError(err?.data?.message || err.message || 'Authentication failed')
              } finally {
                setAuthLoading(false)
              }
            }}>
              {authMode === 'register' && (
                <label>
                  Username
                  <input value={form.username} onChange={(e)=>setForm(f=>({...f, username:e.target.value}))} type="text" placeholder="yourname" required />
                </label>
              )}
              <label>
                Email
                <input value={form.email} onChange={(e)=>setForm(f=>({...f, email:e.target.value}))} type="email" placeholder="you@example.com" required />
              </label>
              <label>
                Password
                <input value={form.password} onChange={(e)=>setForm(f=>({...f, password:e.target.value}))} type="password" placeholder="••••••••" required />
              </label>
              {authError && <div className="form-error">{authError}</div>}
              {authMessage && <div className="form-message">{authMessage}</div>}
              <button type="submit" className="primary" disabled={authLoading}>{authLoading ? 'Please wait…' : (authMode==='login'?'Sign in':'Create account')}</button>
              {authMode === 'login' ? (
                <div className="form-row">
                  <button type="button" className="link" onClick={async ()=>{ if(!form.email) return setAuthError('Enter your email to continue'); try{ await forgotPassword(form.email); setAuthMessage('If your account exists, a reset email has been sent.') } catch(err){ setAuthError(err?.data?.message||err.message) } }}>Forgot password?</button>
                  <button type="button" className="link" onClick={async ()=>{ if(!form.email) return setAuthError('Enter your email to continue'); try{ await resendVerification(form.email); setAuthMessage('Verification email sent (if account exists).') } catch(err){ setAuthError(err?.data?.message||err.message) } }}>Resend verification</button>
                </div>
              ) : null}
              <div className="form-row alt">
                {authMode==='login' ? (
                  <span>New here? <button type="button" className="link" onClick={()=>{setAuthMode('register'); setAuthError(''); setAuthMessage('')}}>Create an account</button></span>
                ) : (
                  <span>Already have an account? <button type="button" className="link" onClick={()=>{setAuthMode('login'); setAuthError(''); setAuthMessage('')}}>Sign in</button></span>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </header>
  )
}
